import { useEffect, useMemo, useState } from "react";
import { formatBRL } from "../../services/billingApi";

const onlyDigits = (value) => String(value || "").replace(/\D/g, "");

const formatCardNumber = (value) =>
  onlyDigits(value).slice(0, 19).replace(/(\d{4})(?=\d)/g, "$1 ").trim();

const formatExpiration = (value) => {
  const digits = onlyDigits(value).slice(0, 6);
  if (digits.length <= 2) return digits;
  return `${digits.slice(0, 2)}/${digits.slice(2)}`;
};

const formatDocument = (value) => {
  const digits = onlyDigits(value).slice(0, 14);
  if (digits.length <= 11) {
    return digits
      .replace(/(\d{3})(\d)/, "$1.$2")
      .replace(/(\d{3})(\d)/, "$1.$2")
      .replace(/(\d{3})(\d{1,2})$/, "$1-$2");
  }
  return digits
    .replace(/(\d{2})(\d)/, "$1.$2")
    .replace(/(\d{3})(\d)/, "$1.$2")
    .replace(/(\d{3})(\d)/, "$1/$2")
    .replace(/(\d{4})(\d{1,2})$/, "$1-$2");
};

/**
 * Formulário de cartão.
 *
 * Os dados sensíveis vão do navegador direto para o Mercado Pago via SDK;
 * o `onSubmit` recebe apenas o token de uso único e os metadados da cobrança.
 */
const CardForm = ({
  amountCents,
  maxInstallments = 1,
  mercadoPago,
  submitting,
  submitLabel = "Pagar agora",
  recurringNotice,
  onSubmit,
}) => {
  const [form, setForm] = useState({
    cardNumber: "",
    cardholderName: "",
    expiration: "",
    securityCode: "",
    identificationNumber: "",
  });
  const [cardInfo, setCardInfo] = useState(null);
  const [installmentOptions, setInstallmentOptions] = useState([]);
  const [installments, setInstallments] = useState(1);
  const [error, setError] = useState(null);

  const bin = useMemo(() => onlyDigits(form.cardNumber).slice(0, 6), [form.cardNumber]);

  // Identifica a bandeira assim que o BIN fica completo.
  useEffect(() => {
    if (!mercadoPago.ready || bin.length < 6) {
      setCardInfo(null);
      return;
    }

    let cancelled = false;

    mercadoPago
      .identifyCard(bin)
      .then((info) => {
        if (!cancelled) setCardInfo(info);
      })
      .catch(() => {
        if (!cancelled) setCardInfo(null);
      });

    return () => {
      cancelled = true;
    };
  }, [bin, mercadoPago]);

  // Busca as parcelas reais só quando o parcelamento é permitido.
  useEffect(() => {
    if (!mercadoPago.ready || bin.length < 6 || maxInstallments <= 1) {
      setInstallmentOptions([]);
      return;
    }

    let cancelled = false;

    mercadoPago
      .fetchInstallments({ cardNumber: bin, amountCents })
      .then((options) => {
        if (cancelled) return;
        setInstallmentOptions(options.filter((o) => o.installments <= maxInstallments));
      })
      .catch(() => {
        if (!cancelled) setInstallmentOptions([]);
      });

    return () => {
      cancelled = true;
    };
  }, [bin, amountCents, maxInstallments, mercadoPago]);

  const update = (field) => (event) => {
    const { value } = event.target;
    setForm((prev) => ({
      ...prev,
      [field]:
        field === "cardNumber"
          ? formatCardNumber(value)
          : field === "expiration"
            ? formatExpiration(value)
            : field === "identificationNumber"
              ? formatDocument(value)
              : field === "securityCode"
                ? onlyDigits(value).slice(0, 4)
                : value,
    }));
  };

  const validate = () => {
    if (onlyDigits(form.cardNumber).length < 13) return "Número do cartão incompleto.";
    if (!form.cardholderName.trim()) return "Informe o nome impresso no cartão.";
    if (onlyDigits(form.expiration).length < 4) return "Validade incompleta.";
    if (onlyDigits(form.securityCode).length < 3) return "Código de segurança incompleto.";

    const document = onlyDigits(form.identificationNumber);
    if (document.length !== 11 && document.length !== 14) {
      return "Informe um CPF ou CNPJ válido.";
    }
    if (!cardInfo?.paymentMethodId) return "Não reconhecemos a bandeira deste cartão.";

    return null;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError(null);

    const validationError = validate();
    if (validationError) {
      setError(validationError);
      return;
    }

    try {
      const [month, year] = form.expiration.split("/");
      const token = await mercadoPago.createCardToken({
        cardNumber: form.cardNumber,
        cardholderName: form.cardholderName,
        expirationMonth: month,
        expirationYear: year,
        securityCode: form.securityCode,
        identificationType: onlyDigits(form.identificationNumber).length > 11 ? "CNPJ" : "CPF",
        identificationNumber: form.identificationNumber,
      });

      await onSubmit({
        token,
        paymentMethodId: cardInfo.paymentMethodId,
        issuerId: cardInfo.issuerId,
        installments,
        payer: { document: form.identificationNumber, name: form.cardholderName },
      });
    } catch (submitError) {
      setError(submitError?.message || "Não foi possível validar o cartão.");
    }
  };

  if (!mercadoPago.ready) {
    return (
      <p className="checkout__muted">
        {mercadoPago.error
          ? `Não foi possível carregar o Mercado Pago: ${mercadoPago.error}`
          : "Carregando pagamento seguro..."}
      </p>
    );
  }

  return (
    <form className="checkout-card-form" onSubmit={handleSubmit}>
      <label className="checkout-field">
        <span>Número do cartão</span>
        <div className="checkout-field__with-icon">
          <input
            inputMode="numeric"
            autoComplete="cc-number"
            placeholder="0000 0000 0000 0000"
            value={form.cardNumber}
            onChange={update("cardNumber")}
          />
          {cardInfo?.thumbnail && (
            <img src={cardInfo.thumbnail} alt={cardInfo.name || "bandeira"} />
          )}
        </div>
      </label>

      <label className="checkout-field">
        <span>Nome impresso no cartão</span>
        <input
          autoComplete="cc-name"
          placeholder="Como está no cartão"
          value={form.cardholderName}
          onChange={update("cardholderName")}
        />
      </label>

      <div className="checkout-field-row">
        <label className="checkout-field">
          <span>Validade</span>
          <input
            inputMode="numeric"
            autoComplete="cc-exp"
            placeholder="MM/AA"
            value={form.expiration}
            onChange={update("expiration")}
          />
        </label>

        <label className="checkout-field">
          <span>CVV</span>
          <input
            inputMode="numeric"
            autoComplete="cc-csc"
            placeholder="123"
            value={form.securityCode}
            onChange={update("securityCode")}
          />
        </label>
      </div>

      <label className="checkout-field">
        <span>CPF ou CNPJ do titular</span>
        <input
          inputMode="numeric"
          placeholder="000.000.000-00"
          value={form.identificationNumber}
          onChange={update("identificationNumber")}
        />
      </label>

      {maxInstallments > 1 && installmentOptions.length > 0 && (
        <label className="checkout-field">
          <span>Parcelamento</span>
          <select
            value={installments}
            onChange={(event) => setInstallments(Number(event.target.value))}
          >
            {installmentOptions.map((option) => (
              <option key={option.installments} value={option.installments}>
                {option.recommended_message ||
                  `${option.installments}x de ${formatBRL(option.installment_amount * 100)}`}
              </option>
            ))}
          </select>
        </label>
      )}

      {recurringNotice && <p className="checkout__notice">{recurringNotice}</p>}

      {error && <p className="checkout__error">{error}</p>}

      <button type="submit" className="button button--primary checkout__submit" disabled={submitting}>
        {submitting ? "Processando..." : `${submitLabel} · ${formatBRL(amountCents)}`}
      </button>

      <p className="checkout__secure">
        Seus dados são enviados criptografados direto ao Mercado Pago. A Shopalli não
        armazena o número do seu cartão.
      </p>
    </form>
  );
};

export default CardForm;
